import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import type { Folder } from '../types';

const LS_FOLDERS_KEY = 'corner:folders';

function loadFoldersFromLS(): Folder[] {
  try {
    const raw = localStorage.getItem(LS_FOLDERS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Folder[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveFoldersToLS(folders: Folder[]): void {
  try {
    localStorage.setItem(LS_FOLDERS_KEY, JSON.stringify(folders));
  } catch {
    console.warn('corner: could not persist folders to localStorage');
  }
}

export interface UseFoldersOptions {
  /** Called when assigning a conversation to a folder (or clearing). Required for assignConversationToFolder. */
  updateConversation: (
    conversationId: string,
    patch: { folderId?: string | null }
  ) => Promise<void>;
  /** Full conversation list; used by deleteFolder to clear folderId on affected conversations. */
  conversationIdsInFolder: (folderId: string) => string[];
  isSignedIn: boolean;
  getToken?: () => string | null;
}

export function useFolders(options: UseFoldersOptions) {
  const { updateConversation, conversationIdsInFolder, isSignedIn, getToken } = options;
  const [folders, setFolders] = useState<Folder[]>(() =>
    isSignedIn ? [] : loadFoldersFromLS()
  );

  // Fetch folders from API when signed in, or load from localStorage
  useEffect(() => {
    if (!isSignedIn || !getToken?.()) {
      setFolders(loadFoldersFromLS());
      return;
    }
    const token = getToken();
    if (!token) return;
    axios
      .get('/api/folders', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        const apiFolders: Folder[] = (res.data.folders ?? []).map(
          (f: { _id: string; name: string; kind?: 'general' | 'contracts'; parentId?: string | null; createdAt?: string; conversationCount?: number }) => ({
            id: String(f._id),
            name: f.name,
            kind: f.kind ?? 'general',
            parentId: f.parentId ?? null,
            createdAt: f.createdAt ? new Date(f.createdAt).getTime() : undefined,
            conversationCount: f.conversationCount ?? 0,
          })
        );
        setFolders(apiFolders);
      })
      .catch(() => {
        // Fall back to localStorage on API failure
        setFolders(loadFoldersFromLS());
      });
  }, [isSignedIn, getToken]);

  const persist = useCallback((next: Folder[]) => {
    setFolders(next);
    saveFoldersToLS(next);
  }, []);

  const createFolder = useCallback(
    async (name: string, kind: 'general' | 'contracts' = 'general', parentId: string | null = null): Promise<string> => {
      const trimmed = name.trim().slice(0, 100) || 'New folder';

      if (isSignedIn && getToken?.()) {
        const token = getToken();
        const res = await axios.post(
          '/api/folders',
          { name: trimmed, kind, parentId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const created = res.data;
        const folder: Folder = {
          id: String(created._id),
          name: created.name,
          kind: created.kind ?? kind,
          parentId: created.parentId ?? parentId,
          createdAt: created.createdAt ? new Date(created.createdAt).getTime() : Date.now(),
        };
        setFolders((prev) => [...prev, folder]);
        return folder.id;
      }

      const id = crypto.randomUUID();
      const next: Folder[] = [
        ...loadFoldersFromLS(),
        { id, name: trimmed, kind, parentId, createdAt: Date.now() },
      ];
      persist(next);
      return id;
    },
    [isSignedIn, getToken, persist]
  );

  const setFolderKind = useCallback(
    async (id: string, kind: 'general' | 'contracts'): Promise<void> => {
      if (isSignedIn && getToken?.()) {
        const token = getToken();
        await axios.patch(
          `/api/folders/${id}`,
          { kind },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFolders((prev) =>
          prev.map((f) => (f.id === id ? { ...f, kind } : f))
        );
        return;
      }

      const next = loadFoldersFromLS().map((f) => (f.id === id ? { ...f, kind } : f));
      persist(next);
    },
    [isSignedIn, getToken, persist]
  );

  const renameFolder = useCallback(
    async (id: string, name: string): Promise<void> => {
      const trimmed = name.trim().slice(0, 100) || 'New folder';

      if (isSignedIn && getToken?.()) {
        const token = getToken();
        await axios.patch(
          `/api/folders/${id}`,
          { name: trimmed },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFolders((prev) =>
          prev.map((f) => (f.id === id ? { ...f, name: trimmed } : f))
        );
        return;
      }

      const next = loadFoldersFromLS().map((f) =>
        f.id === id ? { ...f, name: trimmed } : f
      );
      persist(next);
    },
    [isSignedIn, getToken, persist]
  );

  const deleteFolder = useCallback(
    async (id: string): Promise<void> => {
      if (isSignedIn && getToken?.()) {
        const token = getToken();
        await axios.delete(`/api/folders/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Backend already clears folderId on conversations
        setFolders((prev) => prev.filter((f) => f.id !== id));
        return;
      }

      // localStorage: must clear folderId on conversations manually
      const idsToClear = conversationIdsInFolder(id);
      for (const conversationId of idsToClear) {
        await updateConversation(conversationId, { folderId: null });
      }
      const next = loadFoldersFromLS().filter((f) => f.id !== id);
      persist(next);
    },
    [isSignedIn, getToken, persist, updateConversation, conversationIdsInFolder]
  );

  const assignConversationToFolder = useCallback(
    async (conversationId: string, folderId: string | null): Promise<void> => {
      await updateConversation(conversationId, { folderId });
    },
    [updateConversation]
  );

  return {
    folders,
    createFolder,
    renameFolder,
    deleteFolder,
    setFolderKind,
    assignConversationToFolder,
  };
}
